import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import { 
  Home, 
  Package, 
  Users, 
  User, 
  Settings, 
  BarChart3,
  Plus,
  Clock,
  CheckCircle,
  AlertTriangle,
  Search,
  X,
  MapPin,
  Map
} from 'lucide-react';

const Sidebar = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Fetch dashboard data for quick stats
  const { data: dashboardData } = useQuery(
    'dashboard-overview',
    dashboardAPI.getOverview,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Calculate today's stats using the new API data
  const todayStats = {
    total: dashboardData?.todayShipments || 0,
    inquiry: dashboardData?.todayShipmentsByStatus?.find(s => s.status === 'inquiry')?.count || 0,
    jobs: (dashboardData?.todayShipments || 0) - (dashboardData?.todayShipmentsByStatus?.find(s => s.status === 'inquiry')?.count || 0)
  };

  // Check if user has management access
  const hasManagementAccess = user?.roles && user.roles.some(role => 
    ['management', 'system_administrator'].includes(role.name)
  );

  const hasBatchAccess = user?.roles && user.roles.some(role => ['management', 'system_administrator', 'shipment_agent'].includes(role.name));
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Shipments', href: '/shipments', icon: Package },
    { name: 'New Shipment', href: '/shipments/new', icon: Plus },
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'New Customer', href: '/customers/new', icon: Plus },
    // Only show Users, Cities, Routes, and Settings for management users
    ...(hasManagementAccess ? [
      { name: 'Users', href: '/users', icon: User },
      { name: 'Cities', href: '/cities', icon: MapPin },
      { name: 'Routes', href: '/routes', icon: Map },
      { name: 'Settings', href: '/settings', icon: Settings },
    ] : []),
    // Show Shipment Batches for management, admin, shipment agent
    ...(hasBatchAccess ? [
      { name: 'Shipment Batches', href: '/shipment-batches', icon: Package },
    ] : []),
    { name: 'Reports', href: '/reports', icon: BarChart3 },
  ];

  const quickActions = [
    { name: 'Inquiry', href: '/shipments?status=inquiry', icon: Search, color: 'text-blue-600' },
    { name: 'Pending Shipments', href: '/shipments?status=pending', icon: Clock, color: 'text-yellow-600' },
    { name: 'Completed', href: '/shipments?status=completed', icon: CheckCircle, color: 'text-green-600' },
    { name: 'Overdue', href: '/shipments?status=overdue', icon: AlertTriangle, color: 'text-red-600' },
  ];

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (href === '/shipments') {
      return location.pathname.startsWith('/shipments') && !location.pathname.includes('/new');
    }
    if (href === '/customers') {
      return location.pathname.startsWith('/customers') && !location.pathname.includes('/new');
    }
    return location.pathname === href;
  };

  const handleNavigation = (href) => {
    navigate(href);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="w-60 lg:w-64 xl:w-72 bg-white shadow-lg h-full overflow-y-auto">
      <div className="p-3 lg:p-4 xl:p-6">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <h2 className="text-base lg:text-lg xl:text-xl font-bold text-gray-900">Laksiri Cargo</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 lg:p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors lg:hidden"
            >
              <X className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
          )}
        </div>
        
        {/* Main Navigation */}
        <div className="mb-4 lg:mb-6 xl:mb-8">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 lg:mb-3">
            Navigation
          </h3>
          <nav className="space-y-0.5 lg:space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className={`w-full flex items-center px-2 lg:px-3 py-2 lg:py-2.5 text-sm font-medium rounded-lg lg:rounded-md transition-colors ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 lg:h-4 lg:w-4 mr-2 lg:mr-3 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Quick Actions */}
        <div className="mb-4 lg:mb-6 xl:mb-8">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 lg:mb-3">
            Quick Actions
          </h3>
          <nav className="space-y-0.5 lg:space-y-1">
            {quickActions.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="w-full flex items-center px-2 lg:px-3 py-2 lg:py-2.5 text-sm font-medium text-gray-600 rounded-lg lg:rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Icon className={`h-4 w-4 lg:h-4 lg:w-4 mr-2 lg:mr-3 flex-shrink-0 ${item.color}`} />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Stats Preview */}
        <div className="border-t border-gray-200 pt-3 lg:pt-4 xl:pt-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 lg:mb-3">
            Quick Stats
          </h3>
          <div className="space-y-2 lg:space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Today's Jobs</span>
              <span className="font-medium text-gray-900">{todayStats.total}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Inquiry</span>
              <span className="font-medium text-blue-600">{todayStats.inquiry}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Jobs</span>
              <span className="font-medium text-gray-900">{todayStats.jobs}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 